import { Fragment, FunctionComponent, h } from 'preact';
import * as dayjs from 'dayjs';
import { useCallback, useEffect, useLayoutEffect, useState } from 'preact/hooks';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import EtaService from '../../services/eta-service';
import Translate from '../translate/translate';
import { useTranslation } from '../../hooks/use-translation';

const TIME_FORMAT = 'LT';
export const COUNTDOWN_TIME_THRESHOLD = 30;
const ETA_INTERVAL = 60 * 1000;

export const formatArrivedAt = (time: Date): string[] => {
	const arrivedAt = dayjs(time).format(TIME_FORMAT);
	if (arrivedAt.includes('AM') || arrivedAt.includes('PM')) {
		return arrivedAt.split(' ');
	}

	return [arrivedAt];
};

interface CountDownProps {
	visible: boolean;
	time: Date;
	backgroundColor: string;
	showGreenDelivery?: boolean;
	translations: CountDownTranslations;
}

interface CountDownTranslations {
	title: string;
	minuteSubtitle: string;
}

interface ArrivalTimeProps {
	time: Date;
	backgroundColor: string;
	showGreenDelivery?: boolean;
	translations: ArrivalTimeTranslations;
}

interface ArrivalTimeTranslations {
	title: string;
}

interface EtaProps {
	time: Date;
	translations: Translations;
	backgroundColor?: string;
	className?: string;
	showGreenDelivery?: boolean;
}

export interface Translations {
	arrivalTimeTitle: string;
	countDownTitle: string;
	countDownMinuteSubtitle: string;
}

const CountDown: FunctionComponent<CountDownProps> = ({
	visible,
	time,
	backgroundColor,
	showGreenDelivery,
	translations
}) => {
	const { t } = useTranslation();
	const [animation, setAnimation] = useState('');
	const [etaMinutes, setEtaMinutes] = useState(EtaService.getHowLongFromNow(time));

	useLayoutEffect(() => {
		if (visible) {
			setAnimation('pop');
		}
	}, [etaMinutes]);

	const updateEtaCountDown = useCallback(() => {
		setEtaMinutes(EtaService.getHowLongFromNow(time));
	}, [time]);

	useEffect(() => {
		updateEtaCountDown();
		const interval = setInterval(updateEtaCountDown, ETA_INTERVAL);
		return () => clearInterval(interval);
	}, [time]);

	const clearAnimation = () => {
		setAnimation('');
	};

	return (
		<div className="count-down side-b" style={{ backgroundColor }}>
			{showGreenDelivery && (
				<span data-testid="green-delivery-icon">
					<BringgIcon className="green-delivery-icon" iconName={BringgFontIcons.LeafEco}></BringgIcon>
				</span>
			)}
			{etaMinutes > 0 ? (
				<Fragment>
					<div className="title" aria-label={t('TRACKING.ESTIMATED_TIME_ARRIVAL')}>
						{translations.title}
					</div>
					<div className={`time ${animation}`} onAnimationEnd={clearAnimation}>
						{etaMinutes}
					</div>
					<div className="unit">{translations.minuteSubtitle}</div>
				</Fragment>
			) : (
				<span className="soon">
					<Translate text="SOON" />
				</span>
			)}
		</div>
	);
};

const ArrivalTime: FunctionComponent<ArrivalTimeProps> = ({
	time,
	backgroundColor,
	showGreenDelivery,
	translations
}) => {
	const [arrivedAt, amPm] = formatArrivedAt(time);
	return (
		<div className={classNames('arrival-time side-a', { 'arrival-time-am-pm': amPm })} style={{ backgroundColor }}>
			{showGreenDelivery && (
				<span data-testid="green-delivery-icon">
					<BringgIcon className="green-delivery-icon" iconName={BringgFontIcons.LeafEco}></BringgIcon>
				</span>
			)}
			<div className="title">{translations.title}</div>
			<div className="time">{arrivedAt}</div>
			{amPm && <div className="time-am-pm">{amPm}</div>}
		</div>
	);
};

const Eta: FunctionComponent<EtaProps> = ({
	time,
	translations,
	backgroundColor,
	className,
	showGreenDelivery
}: EtaProps) => {
	const { t } = useTranslation();
	const [flipped, setFlipped] = useState(false);
	const flipIfPassedThreshold = useCallback(() => {
		if (EtaService.getHowLongFromNow(time) <= COUNTDOWN_TIME_THRESHOLD) {
			setFlipped(true);
		}
	}, [time]);

	useEffect(() => {
		flipIfPassedThreshold();
		const etaFlipInterval = setInterval(flipIfPassedThreshold, ETA_INTERVAL);
		return () => clearInterval(etaFlipInterval);
	}, [time]);

	const onClick = () => {
		setFlipped(!flipped);
	};

	const getContainerARIALabel = () => {
		const [arrivedAt, amPm] = formatArrivedAt(time);
		const timeValue = flipped
			? `${EtaService.getHowLongFromNow(time)} ${translations.countDownMinuteSubtitle || ''}`
			: `${arrivedAt} ${amPm || ''}`;
		return `${t('TRACKING.ARRIVING_IN_ARIA_LABEL')} ${timeValue}`;
	};

	return (
		<div
			className={classNames('map-eta-container', className)}
			data-testid="eta"
			aria-label={getContainerARIALabel()}
			tabIndex={0}
		>
			<div className={classNames('eta', { flipped })} onClick={onClick} aria-hidden={true}>
				<CountDown
					visible={flipped}
					time={time}
					backgroundColor={backgroundColor}
					showGreenDelivery={showGreenDelivery}
					translations={{
						title: translations.countDownTitle,
						minuteSubtitle: translations.countDownMinuteSubtitle
					}}
				/>
				<ArrivalTime
					time={time}
					backgroundColor={backgroundColor}
					showGreenDelivery={showGreenDelivery}
					translations={{ title: translations.arrivalTimeTitle }}
				/>
			</div>
		</div>
	);
};

export default Eta;
